import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '../core/environment';

interface Lookup { id: string; name: string; }
interface PaymentSummary {
  totalCollected: number;
  pendingDues: number;
  averageFee: number;
  overdueAmount: number;
  overdueAccounts: number;
}

interface PaymentRecent {
  id: string;
  receiptNumber: string;
  payerName: string;
  payerType: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  status: string;
  paymentDate: string;
  paymentMode: string;
}

interface TransactionRow {
  receipt: string;
  payer: string;
  payerType: string;
  initials: string;
  amount: number;
  balance: number;
  status: string;
  mode: string;
  date: string;
}

@Component({
  selector: 'app-fees',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="fees-page">
      <section class="fees-header">
        <div>
          <span>Financial Overview</span>
          <h2>Fees & Payments</h2>
        </div>
        <div class="fees-header-actions">
          <button type="button">Export Report</button>
          <button class="primary" type="button">Bulk Invoicing</button>
        </div>
      </section>

      <section class="fees-kpis">
        <article>
          <span>Total Collected</span>
          <strong>Rs {{ money(summary().totalCollected) }}</strong>
          <p>+12% vs last month</p>
        </article>
        <article>
          <span>Pending Dues</span>
          <strong>Rs {{ money(summary().pendingDues) }}</strong>
          <p>{{ pendingCount() }} payments awaiting balance</p>
        </article>
        <article>
          <span>Average Fee</span>
          <strong>Rs {{ money(summary().averageFee) }}</strong>
          <p>Across all programs</p>
        </article>
        <article class="danger">
          <span>Overdue Accounts</span>
          <strong>Rs {{ money(summary().overdueAmount) }}</strong>
          <p>{{ summary().overdueAccounts }} accounts over due date</p>
        </article>
      </section>

      <section class="fees-layout">
        <article class="fees-transactions">
          <div class="fees-card-heading">
            <div>
              <h3>Recent Transactions</h3>
              <p>Latest payments and outstanding balances.</p>
            </div>
            <select>
              <option>All Status</option>
              <option>Paid</option>
              <option>Partial</option>
              <option>Overdue</option>
            </select>
          </div>

          <table>
            <thead>
              <tr><th>Receipt No</th><th>Payer</th><th>Paid</th><th>Balance</th><th>Status</th><th>Mode</th></tr>
            </thead>
            <tbody>
              @for (row of transactions(); track row.receipt) {
                <tr>
                  <td><strong>{{ row.receipt }}</strong></td>
                  <td>
                    <div class="fee-student">
                      <span>{{ row.initials }}</span>
                      <div>
                        <strong>{{ row.payer }}</strong>
                        <small>{{ row.payerType }} | {{ row.date }}</small>
                      </div>
                    </div>
                  </td>
                  <td>Rs {{ money(row.amount) }}</td>
                  <td [class.due-amount]="row.balance > 0">Rs {{ money(row.balance) }}</td>
                  <td><span class="fee-status" [class]="row.status.toLowerCase()">{{ row.status }}</span></td>
                  <td>{{ row.mode }}</td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="6"><div class="guest-empty">No payment transactions yet. Collect the first fee.</div></td>
                </tr>
              }
            </tbody>
          </table>

          <button class="view-transactions" type="button">View All Transactions</button>
        </article>

        <aside class="fees-side">
          <form class="collect-card" [formGroup]="form" (ngSubmit)="collect()">
            <h3>Collect New Fee</h3>

            <label>Payer Type
              <select formControlName="payerType">
                <option [value]="1">Student</option>
                <option [value]="2">Staff</option>
                <option [value]="3">Guest</option>
              </select>
            </label>

            <label>Payer / Student Id
              <input formControlName="payerId" placeholder="Search by name or paste ID">
            </label>

            <div class="two-col">
              <label>Total Amount
                <input type="number" formControlName="totalAmount" placeholder="0.00">
              </label>
              <label>Paid Amount
                <input type="number" formControlName="paidAmount" placeholder="0.00">
              </label>
            </div>

            <label>Payment Mode
              <select formControlName="paymentModeId">
                @for (mode of modes(); track mode.id) {
                  <option [value]="mode.id">{{ mode.name }}</option>
                }
              </select>
            </label>

            <label>Notes
              <textarea formControlName="notes" placeholder="Receipt note or payment reference"></textarea>
            </label>

            <button class="collect-submit" type="submit" [disabled]="form.invalid">Generate Receipt & Pay</button>
          </form>

          <article class="receipt-preview">
            <div class="receipt-heading">
              <h3>Receipt Preview</h3>
              <span>Print</span>
            </div>
            <div class="receipt-paper">
              <strong>PERKS SPORTS ACADEMY</strong>
              <small>Receipt #REC-TEMP-01</small>
              <dl>
                <div><dt>Payer</dt><dd>{{ form.controls.payerId.value || '-- Select Payer --' }}</dd></div>
                <div><dt>Date</dt><dd>{{ today }}</dd></div>
                <div><dt>Amount Paid</dt><dd>Rs {{ form.controls.paidAmount.value || 0 }}</dd></div>
                <div><dt>Balance</dt><dd>Rs {{ balanceAmount() }}</dd></div>
              </dl>
              <p>Thank you for your payment. Digital receipt will be sent to registered email.</p>
            </div>
          </article>
        </aside>
      </section>
    </div>
  `
})
export class FeesComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  readonly modes = signal<Lookup[]>([]);
  readonly summary = signal<PaymentSummary>({
    totalCollected: 0,
    pendingDues: 0,
    averageFee: 0,
    overdueAmount: 0,
    overdueAccounts: 0
  });
  readonly transactions = signal<TransactionRow[]>([]);
  readonly today = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date());

  readonly form = this.fb.nonNullable.group({
    payerType: [1, Validators.required],
    payerId: ['', Validators.required],
    enrollmentId: [null as string | null],
    guestBookingId: [null as string | null],
    summerCampRegistrationId: [null as string | null],
    totalAmount: [0, Validators.required],
    paidAmount: [0, Validators.required],
    dueDate: [null as string | null],
    paymentModeId: ['', Validators.required],
    notes: ['']
  });

  ngOnInit(): void {
    this.http.get<Lookup[]>(`${environment.apiUrl}/masters/payment-modes`).subscribe({
      next: (modes) => {
        this.modes.set(modes);
        this.form.patchValue({ paymentModeId: modes[0]?.id ?? '' });
      },
      error: () => this.modes.set([])
    });
    this.loadPayments();
  }

  pendingCount(): number {
    return this.transactions().filter((row) => row.balance > 0).length;
  }

  balanceAmount(): number {
    const value = this.form.getRawValue();
    return Math.max(0, Number(value.totalAmount || 0) - Number(value.paidAmount || 0));
  }

  collect(): void {
    if (this.form.invalid) return;
    this.http.post(`${environment.apiUrl}/payments/collect`, this.form.getRawValue()).subscribe(() => {
      this.loadPayments();
    });
  }

  money(value: number): string {
    return Math.round(Number(value || 0)).toLocaleString('en-IN');
  }

  private loadPayments(): void {
    this.http.get<PaymentSummary>(`${environment.apiUrl}/payments/summary`).subscribe({
      next: (summary) => this.summary.set(summary)
    });

    this.http.get<PaymentRecent[]>(`${environment.apiUrl}/payments/recent`).subscribe({
      next: (rows) => this.transactions.set(rows.map((row) => this.mapTransaction(row)))
    });
  }

  private mapTransaction(row: PaymentRecent): TransactionRow {
    return {
      receipt: row.receiptNumber === '-' ? `#${row.id.slice(0, 8).toUpperCase()}` : row.receiptNumber,
      payer: row.payerName,
      payerType: row.payerType,
      initials: this.initials(row.payerName),
      amount: row.paidAmount,
      balance: row.balanceAmount,
      status: row.balanceAmount > 0 && row.status === 'Pending' ? 'Overdue' : row.status,
      mode: row.paymentMode,
      date: new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(new Date(row.paymentDate))
    };
  }

  private initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? 'P') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
}
